import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Keyboard from "react-simple-keyboard";
import "react-simple-keyboard/build/css/index.css";

const KB_LAYOUT = {
  default: [
    "1 2 3 4 5 6 7 8 9 0 {bksp}",
    "q w e r t y u i o p",
    "a s d f g h j k l {enter}",
    "{shift} z x c v b n m , . {shift}",
    "{numbers} @ {space} - {close}",
  ],
  shift: [
    "! @ # $ % ^ & * ( ) {bksp}",
    "Q W E R T Y U I O P",
    "A S D F G H J K L {enter}",
    "{shift} Z X C V B N M ; : {shift}",
    "{numbers} _ {space} + {close}",
  ],
  numbers: [
    "1 2 3 4 5 6 7 8 9 0 {bksp}",
    "- / : ; ( ) $ & @ \"",
    ". , ? ! ' {enter}",
    "{abc} _ {space} + {close}",
  ],
};

const KB_DISPLAY = {
  "{bksp}": "⌫",
  "{enter}": "↵ enter",
  "{shift}": "⇧",
  "{space}": "space",
  "{numbers}": "123",
  "{abc}": "abc",
  "{close}": "✕",
};

const KB_BUTTON_THEME = [
  { class: "hg-action", buttons: "{enter} {bksp} {shift} {numbers} {abc} {close}" },
];

/**
 * Floating on-screen keyboard for touchscreen kiosks.
 * Auto-shows when any <input> or <textarea> is focused, hides when blurred.
 * Types into the focused element via native input events.
 */
export default function TouchKeyboard() {
  const [visible, setVisible] = useState(false);
  const [layoutName, setLayoutName] = useState("default");
  const targetRef = useRef(null);
  const kbRef = useRef(null);

  useEffect(() => {
    function isTextInput(el) {
      if (!el) return false;
      if (el.tagName === "TEXTAREA") return true;
      if (el.tagName !== "INPUT") return false;
      const t = (el.type || "text").toLowerCase();
      // Show for most text-like inputs; skip date/time/file/button etc.
      return ["text", "search", "email", "url", "tel", "password", "number"].includes(t);
    }

    function onFocusIn(e) {
      if (isTextInput(e.target)) {
        targetRef.current = e.target;
        // Sync keyboard buffer with current value
        if (kbRef.current) kbRef.current.setInput(e.target.value || "");
        setVisible(true);
      }
    }
    function onFocusOut(e) {
      // Defer so clicking a key doesn't immediately blur+hide.
      setTimeout(() => {
        const active = document.activeElement;
        if (!active || !isTextInput(active)) {
          setVisible(false);
          targetRef.current = null;
        }
      }, 100);
    }

    document.addEventListener("focusin", onFocusIn);
    document.addEventListener("focusout", onFocusOut);
    return () => {
      document.removeEventListener("focusin", onFocusIn);
      document.removeEventListener("focusout", onFocusOut);
    };
  }, []);

  function setNativeValue(el, value) {
    const proto = el.tagName === "TEXTAREA" ? window.HTMLTextAreaElement.prototype : window.HTMLInputElement.prototype;
    const setter = Object.getOwnPropertyDescriptor(proto, "value")?.set;
    setter?.call(el, value);
    el.dispatchEvent(new Event("input", { bubbles: true }));
  }

  function onChange(input) {
    const el = targetRef.current;
    if (!el) return;
    setNativeValue(el, input);
  }

  function onKeyPress(button) {
    if (button === "{shift}" || button === "{lock}") {
      setLayoutName((n) => (n === "default" ? "shift" : "default"));
      return;
    }
    if (button === "{numbers}") return setLayoutName("numbers");
    if (button === "{abc}") return setLayoutName("default");
    if (button === "{enter}") {
      const el = targetRef.current;
      if (!el) return;
      // Submit nearest form, else blur
      const form = el.form;
      if (form) {
        if (typeof form.requestSubmit === "function") form.requestSubmit();
        else form.dispatchEvent(new Event("submit", { cancelable: true, bubbles: true }));
      } else {
        el.blur();
      }
      setVisible(false);
    }
    if (button === "{close}") {
      targetRef.current?.blur();
      setVisible(false);
    }
  }

  if (!visible) return null;

  return createPortal(
    <div
      data-testid="touch-keyboard"
      data-touch-keyboard="true"
      className="fixed bottom-0 left-0 right-0 p-3"
      style={{
        zIndex: 300, // above Radix Dialog overlay (z-50) and DialogContent (z-50)
        pointerEvents: "auto", // Radix Dialog sets pointer-events:none on <body>; override here
        background: "rgba(23, 21, 20, 0.96)",
        backdropFilter: "blur(8px)",
        borderTop: "1px solid #33302C",
        boxShadow: "0 -20px 60px rgba(0,0,0,0.6)",
      }}
      onMouseDown={(e) => e.preventDefault()} // keep focus on the target input
      onPointerDown={(e) => e.preventDefault()} // touchscreen equivalent
    >
      <Keyboard
        keyboardRef={(r) => (kbRef.current = r)}
        layoutName={layoutName}
        onChange={onChange}
        onKeyPress={onKeyPress}
        theme="hg-theme-default hg-layout-default hearthKeyboard"
        layout={KB_LAYOUT}
        display={KB_DISPLAY}
        buttonTheme={KB_BUTTON_THEME}
      />
      <style>{`
        .hearthKeyboard.hg-theme-default {
          background: transparent;
          font-family: "Satoshi", system-ui, sans-serif;
        }
        .hearthKeyboard .hg-row { display: flex; gap: 6px; margin-bottom: 6px; }
        .hearthKeyboard .hg-button {
          background: #23211F !important;
          color: #EAE4DB !important;
          border: 1px solid #33302C !important;
          border-radius: 12px !important;
          box-shadow: none !important;
          height: 56px !important;
          font-size: 20px !important;
          font-weight: 500 !important;
          flex: 1;
          min-width: 0;
          transition: background-color 80ms ease;
        }
        .hearthKeyboard .hg-button:active {
          background: #D6A85B !important;
          color: #171514 !important;
        }
        .hearthKeyboard .hg-button.hg-action {
          background: #2D2A28 !important;
          color: #A6A098 !important;
          font-size: 16px !important;
          flex: 1.4;
        }
        .hearthKeyboard .hg-button[data-skbtn="{space}"] { flex: 5; }
        .hearthKeyboard .hg-button[data-skbtn="{enter}"] { flex: 1.6; }
      `}</style>
    </div>,
    document.body
  );
}
